<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(defineProps<{
  phase: "loading" | "ready";
  progress?: number | null;
}>(), {
  progress: null,
});

const emit = defineEmits<{ (e: "enter"): void }>();

const isCoarse = window.matchMedia("(pointer: coarse)").matches;
const pct = computed(() => (props.progress == null ? null : Math.round(props.progress * 100)));

const onClick = () => {
  if (props.phase === "ready") emit("enter");
};
</script>

<template>
  <div
    class="fixed inset-0 z-20 flex items-center justify-center select-none outline-none"
    :class="phase === 'ready' ? 'cursor-pointer' : ''"
    role="button"
    :tabindex="phase === 'ready' ? 0 : -1"
    :aria-label="phase === 'ready' ? (isCoarse ? 'Tap to enter' : 'Click to enter') : 'Loading'"
    @click="onClick"
    @keydown.enter.prevent="onClick"
    @keydown.space.prevent="onClick"
  >
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-700 ease-out"
      enter-from-class="opacity-0 translate-y-4"
      leave-active-class="transition-all duration-500 ease-in"
      leave-to-class="opacity-0 -translate-y-3"
    >
      <div v-if="phase === 'loading'" key="loading" class="relative flex items-center justify-center text-center px-6">
        <p class="m-0 font-semibold uppercase tracking-[0.28em] sm:tracking-[0.36em] text-[clamp(24px,3.5vw,40px)] text-[var(--color-dark,#491214)] whitespace-nowrap">
          loading<span v-if="pct !== null"> · {{ pct }}%</span>
        </p>
      </div>

      <div
        v-else
        key="ready"
        class="relative flex items-center justify-center text-center px-6"
      >
        <div class="cta-container relative inline-flex items-center justify-center">
          <div class="fog-backdrop" aria-hidden="true"></div>
          <span
            class="cta-text relative z-10 m-0 font-semibold uppercase tracking-[0.28em] sm:tracking-[0.36em] text-[clamp(20px,2.8vw,32px)] text-[var(--color-dark,#491214)] whitespace-nowrap [text-shadow:_0_0_16px_rgba(255,255,255,0.95),_0_0_6px_rgba(255,255,255,0.9)]"
          >
            {{ isCoarse ? 'tap to enter' : 'click to enter' }}
          </span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.cta-container {
  animation: cta-breathe 2.8s ease-in-out infinite;
  transition: transform 0.3s ease;
}

.cta-container:hover {
  transform: scale(1.03);
}

.fog-backdrop {
  position: absolute;
  inset: -24px -56px;
  border-radius: 9999px;
  background:
    radial-gradient(
      ellipse 65% 55% at 50% 50%,
      rgba(255, 255, 255, 0.92) 0%,
      rgba(255, 255, 255, 0.82) 30%,
      rgba(255, 255, 255, 0.45) 55%,
      rgba(255, 255, 255, 0.12) 75%,
      rgba(255, 255, 255, 0) 90%
    ),
    radial-gradient(
      ellipse 85% 75% at 50% 50%,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.2) 45%,
      rgba(255, 255, 255, 0.06) 70%,
      rgba(255, 255, 255, 0.01) 88%,
      rgba(255, 255, 255, 0) 100%
    );
  pointer-events: none;
  transform: translateZ(0);
}

@media (min-width: 640px) {
  .fog-backdrop {
    inset: -30px -70px;
  }
}

@keyframes cta-breathe {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.86;
    transform: scale(0.985);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cta-container {
    animation: none;
  }
}
</style>